import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Users, UserCheck, Building2, Calendar, Clock, LogOut } from 'lucide-react';
import { visitorAPI } from '../../services/api';
import { toast } from 'sonner';

export function VisitorsModal({ showVisitorsModal, setShowVisitorsModal, visitors, onRefresh }) {
  const checkout = async (id) => {
    if (!window.confirm('Mark this visitor as exited?')) return;
    try {
      await visitorAPI.checkout(id);
      toast.success('Visitor checked out');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check out');
    }
  };
  return (
    <Dialog open={showVisitorsModal} onOpenChange={setShowVisitorsModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            All Visitors ({visitors.length})
          </DialogTitle>
          <DialogDescription>Complete list of visitors to your villa</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {visitors.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No visitors yet</p>
          ) : (
            visitors.map((visitor) => {
              const isInside = !visitor.exit_time && (visitor.status === 'approved' || visitor.status === 'pending');
              return (
                <div key={visitor.id} data-testid={`visitor-list-${visitor.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{visitor.name}</p>
                    <p className="text-sm text-slate-600 truncate">{visitor.phone}</p>
                    <p className="text-xs text-slate-500">In: {new Date(visitor.entry_time).toLocaleString()}</p>
                    {visitor.exit_time && (
                      <p className="text-xs text-emerald-600">Out: {new Date(visitor.exit_time).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge variant={visitor.status === 'approved' ? 'default' : 'secondary'} className={visitor.exit_time ? 'bg-slate-400' : (visitor.status === 'approved' ? 'bg-emerald-500' : 'bg-amber-500')}>
                      {visitor.exit_time ? 'exited' : visitor.status}
                    </Badge>
                    {isInside && (
                      <Button
                        data-testid={`visitor-checkout-${visitor.id}`}
                        size="sm" variant="outline"
                        onClick={() => checkout(visitor.id)}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-7 text-xs"
                      >
                        <LogOut className="w-3 h-3 mr-1" /> Mark Exited
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StaffModal({
  showStaffModal,
  setShowStaffModal,
  myStaff,
  activeStaffOnCampus
}) {
  return (
    <Dialog open={showStaffModal} onOpenChange={setShowStaffModal}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="staff-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-gk-teal" />
            My Staff/Maids
          </DialogTitle>
          <DialogDescription>{myStaff.length} assigned, {activeStaffOnCampus.length} currently on campus</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {activeStaffOnCampus.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gk-navy mb-2 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Currently on Campus ({activeStaffOnCampus.length})
              </h4>
              <div className="space-y-2">
                {activeStaffOnCampus.map((entry) => (
                  <div key={entry.id} data-testid={`active-staff-entry-${entry.id}`} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{entry.staff_name}</p>
                        <p className="text-xs text-slate-500">Check-in: {new Date(entry.check_in_time).toLocaleString()}</p>
                        <p className="text-xs text-slate-400">Pass: {entry.entry_pass_number}</p>
                      </div>
                      <Badge className="bg-emerald-500">Active</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Assigned Staff</h4>
            {myStaff.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No staff assigned to your villa</p>
            ) : (
              <div className="space-y-2">
                {myStaff.map((staffMember) => (
                  <div key={staffMember.id} data-testid={`staff-item-${staffMember.id}`} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{staffMember.name}</p>
                        <p className="text-sm text-slate-600">{staffMember.job_description}</p>
                        <p className="text-xs text-slate-500">Phone: {staffMember.phone}</p>
                        {staffMember.employee_id && <p className="text-xs text-slate-400">EMP: {staffMember.employee_id}</p>}
                      </div>
                      <Badge variant="outline">{staffMember.id_type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddCommunityModal({
  showAddCommunityModal,
  setShowAddCommunityModal,
  communitySearch,
  handleSearchCommunity,
  communityResults,
  handleRequestJoinCommunity
}) {
  return (
    <Dialog open={showAddCommunityModal} onOpenChange={setShowAddCommunityModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gk-teal" />
            Join a Community
          </DialogTitle>
          <DialogDescription>Search and request to join a community</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="relative">
            <input
              data-testid="dashboard-community-search"
              value={communitySearch}
              onChange={(e) => handleSearchCommunity(e.target.value)}
              placeholder="Search community name..."
              className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
            />
            <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {communityResults.length === 0 && communitySearch.length > 0 && (
              <p className="text-sm text-slate-500 text-center py-3">No communities found</p>
            )}
            {communityResults.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-gk-bg transition-colors">
                <div><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-slate-500">{c.address}</p></div>
                <Button size="sm" onClick={() => handleRequestJoinCommunity(c.id)} className="bg-gk-teal hover:bg-gk-teal-hover">Request</Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
